import type { MarkType, Node as PMNode, ResolvedPos } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import type { CommentMarkPosition } from "ls/features/disko/api/storage.types";
import type { ContentNodeWithPos, findChildrenByType } from "prosemirror-utils";
import Link from "@tiptap/extension-link";
import { BulletList, ListItem, OrderedList, TaskItem, TaskList } from "@tiptap/extension-list";
import { Table } from "@tiptap/extension-table";
import { Fragment, Mark } from "@tiptap/pm/model";
import { CalloutItemNodeName } from "./callout/types";
import { SlashCommandsPluginKey } from "./commands";
import { EmojiSuggestionPluginKey } from "./emojis";
import { MentionSuggestionPluginKey } from "./mention";

export type FindNodesResult = ReturnType<typeof findChildrenByType>;
export type FindPredicate = (node: PMNode) => boolean;
export type FindResult = ContentNodeWithPos | undefined;

export function canCreateLinkInRange(from: number, to: number) {
  return (state: EditorState) => {
    const link = state.schema.marks.link;
    if (!link) return false;

    return canCreateMarkInRange(link, from, to, state);
  };
}

export function canCreateMarkInRange(markType: MarkType, from: number, to: number, state: EditorState) {
  if (!state.doc.rangeHasMark(from, to, markType)) {
    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);
    if ($from.parent === $to.parent && $from.parent.isTextblock) {
      if ($from.parent.type.allowsMarkType(markType)) {
        let allowed = true;
        state.doc.nodesBetween(from, to, node => {
          allowed = allowed && !node.marks.some(m => {
            return m.type.excludes(markType);
          });
          return allowed;
        });
        return allowed;
      }
    }
  }
  return false;
}

export function getCommentMarkPositions(from: number, to: number, state: EditorState) {
  let currentPath: number[] = [];
  const positions: CommentMarkPosition[] = [];
  let inlineJustAdded = false;
  state.doc.nodesBetween(from, to, (node, pos, _, index) => {
    currentPath.push(index);
    if (node.isInline) {
      const textFrom = node.isText ? Math.max(0, from - pos) : 0;
      const textTo = node.isText ? Math.min(to - pos, node.nodeSize) : 0;
      const path = !inlineJustAdded || !positions.length
        ? [...currentPath]
        : [...positions[positions.length - 1].path.slice(0, -1), index]; // when more then one inline nodes inside one parent, take path from prev node
      positions.push({
        from: textFrom,
        to: textTo,
        path,
      });
      currentPath = [];
    }
    inlineJustAdded = node.isInline;
  });

  return positions;
}

const suggestionPluginsKeys = [
  SlashCommandsPluginKey,
  EmojiSuggestionPluginKey,
  MentionSuggestionPluginKey,
];

export function isAnySuggestionActive(state: EditorState) {
  return suggestionPluginsKeys.some(plugin => {
    const pluginState = plugin.getState(state);
    return pluginState?.active;
  });
}

export function isListNode(node: PMNode) {
  const nodeTypeName = node.type.name;
  return nodeTypeName === OrderedList.name || nodeTypeName === BulletList.name || nodeTypeName === TaskList.name;
}

export function isListItemNode(node: PMNode) {
  const nodeTypeName = node.type.name;
  return nodeTypeName === ListItem.name || nodeTypeName === TaskItem.name;
}

export function isCalloutItemNode(node: PMNode) {
  const nodeTypeName = node.type.name;
  return nodeTypeName === CalloutItemNodeName;
}

export function isTableNode(node: PMNode) {
  const nodeTypeName = node.type.name;
  return nodeTypeName === Table.name;
}

export interface LinkMarkAttributes {
  href: string;
}

export type LinkMark = Mark & {
  attrs: LinkMarkAttributes;
};

export function isLinkMark(mark: Mark): mark is LinkMark {
  return mark instanceof Mark && mark.type.name === Link.name;
}

export function findTopMostNodeClosestToPos($pos: ResolvedPos, predicate: FindPredicate): FindResult {
  for (let i = 0; i <= $pos.depth; i++) {
    const node = $pos.node(i);
    if (predicate(node)) {
      return {
        pos: i > 0 ? $pos.before(i) : 0,
        start: $pos.start(i),
        depth: i,
        node,
      };
    }
  }
  return undefined;
}

export function mapChildren<T>(
  node: PMNode | Fragment,
  callback: (child: PMNode, index: number, frag: Fragment) => T,
): T[] {
  const array = [];
  for (let i = 0; i < node.childCount; i++) {
    array.push(
      callback(
        node.child(i),
        i,
        node instanceof Fragment ? node : node.content,
      ),
    );
  }

  return array;
}
